<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { DialogContentEmits, DialogContentProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cva } from 'class-variance-authority'

export const sheetVariants = cva(
  'fixed flex h-full flex-col overflow-y-auto bg-white font-display shadow-2xl transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-200 data-[state=open]:duration-300',
  {
    variants: {
      side: {
        left: 'inset-y-0 left-0 border-r border-default data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
        right:
          'inset-y-0 right-0 border-l border-default data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
      },
      size: {
        xs: 'w-full max-w-[350px]',
        sm: 'w-full max-w-[560px]',
        md: 'w-full max-w-[730px]',
        lg: 'w-full max-w-[1110px]',
      },
    },
    defaultVariants: {
      side: 'right',
      size: 'md',
    },
  },
)

export type SheetSide = NonNullable<VariantProps<typeof sheetVariants>['side']>
export type SheetSize = NonNullable<VariantProps<typeof sheetVariants>['size']>

export type SheetContentProps = DialogContentProps & {
  side?: SheetSide
  size?: SheetSize
  overlay?: boolean
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import type { OverlayStackSlot } from '#ui-kit/lib/useOverlayStack'
import { reactiveOmit, unrefElement } from '@vueuse/core'
import { DialogContent, injectDialogRootContext, useForwardPropsEmits } from 'reka-ui'
import { computed, mergeProps, onUnmounted, provide, shallowRef, useTemplateRef, watch } from 'vue'
import {
  OVERLAY_BACKDROP_Z_KEY,
  OVERLAY_Z_BASE,
  OVERLAY_Z_PANEL_OFFSET,
  useOverlayStack,
} from '#ui-kit/lib/useOverlayStack'
import { cn } from '#ui-kit/lib/utils'
import SheetOverlay from './SheetOverlay.vue'
import { isSheetDismissIntent } from './sheet-dismiss'
import SheetPortal from './SheetPortal.vue'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<SheetContentProps>(), {
  overlay: true,
})
const emits = defineEmits<DialogContentEmits>()

const delegated = reactiveOmit(props, 'class', 'side', 'size', 'overlay')
const forwarded = useForwardPropsEmits(delegated, emits)

// Overlay z-index stack — acquire a slot when the sheet opens and release
// when it closes (or unmounts). Backdrop and panel z-indexes auto-bump for
// each nested overlay opened on top of this one.
const rootContext = injectDialogRootContext()
const { acquire } = useOverlayStack()
const slot = shallowRef<OverlayStackSlot | null>(null)

watch(
  () => rootContext.open.value,
  (isOpen) => {
    if (isOpen) {
      if (!slot.value) {
        slot.value = acquire()
      }
    } else if (slot.value) {
      slot.value.release()
      slot.value = null
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  if (slot.value) {
    slot.value.release()
    slot.value = null
  }
})

const backdropZ = computed(() => slot.value?.backdropZ.value ?? OVERLAY_Z_BASE)
const panelZ = computed(() => slot.value?.panelZ.value ?? OVERLAY_Z_BASE + OVERLAY_Z_PANEL_OFFSET)

provide(OVERLAY_BACKDROP_Z_KEY, backdropZ)

const overlayRef = useTemplateRef('overlay')

// Runs before the caller's handler so it can tell a dismiss of this sheet by `defaultPrevented`.
function onInteractOutside(event: Event) {
  if (!isSheetDismissIntent(event, unrefElement(overlayRef))) {
    event.preventDefault()
  }
}

const contentProps = computed(() => mergeProps({ onInteractOutside }, forwarded.value))
</script>

<template>
  <SheetPortal>
    <SheetOverlay v-if="overlay" ref="overlay" />
    <DialogContent
      v-bind="{ ...contentProps, ...$attrs }"
      :class="cn(sheetVariants({ side: props.side ?? 'right', size: props.size ?? 'md' }), props.class)"
      :style="{ zIndex: panelZ }"
    >
      <slot />
    </DialogContent>
  </SheetPortal>
</template>
